import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, MailCheck, RefreshCw, Square } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { MailConfigCard } from "@/components/mailserials/MailConfigCard";
import { MailLogTable } from "@/components/mailserials/MailLogTable";
import { DetailedCheckCard } from "@/components/mailserials/DetailedCheckCard";

export default function LetturaMailPage() {
  const [config, setConfig] = useState(null);
  const [log, setLog] = useState([]);
  const [techFilter, setTechFilter] = useState("all");
  const [techs, setTechs] = useState([]);

  useEffect(() => {
    api.get("/users", { params: { role: "tecnico,subappalto" } }).then((r) => setTechs(r.data || [])).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    const params = techFilter === "all" ? {} : { technician_id: techFilter };
    const [c, l] = await Promise.all([api.get("/mail-serials/config"), api.get("/mail-serials/log", { params })]);
    setConfig(c.data);
    setLog(l.data?.items || []);
  }, [techFilter]);

  useEffect(() => { load().catch(() => toast.error("Caricamento non riuscito")); }, [load]);
  useEffect(() => {
    if (!config?.running) return undefined;
    const iv = setInterval(() => { load().catch(() => {}); }, 4000);
    return () => clearInterval(iv);
  }, [config?.running, load]);

  const runNow = async () => {
    try {
      await api.post("/mail-serials/run");
      toast.success("Lettura avviata");
      setConfig((c) => ({ ...c, running: true }));
      setTimeout(() => load().catch(() => {}), 1500);
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Avvio non riuscito"); }
  };

  const stopNow = async () => {
    try {
      await api.post("/mail-serials/stop");
      toast.success("Lettura interrotta: le mail già elaborate restano salvate");
      load().catch(() => {});
    } catch { toast.error("Interruzione non riuscita"); }
  };

  return (
    <div className="space-y-6" data-testid="lettura-mail-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="flex items-center gap-3 text-3xl font-black tracking-tight text-slate-900"><MailCheck className="h-7 w-7 text-[#0052FF]" /> Lettura Mail Seriali</h1>
          <p className="mt-2 max-w-3xl text-sm text-slate-600">
            Il software legge solo le mail <b>non lette</b> dei tecnici selezionati (dopo averle elaborate le segna come lette in Gmail; se non trova seriali la lascia da leggere) e prende i seriali che iniziano con <b>%</b>, quelli scritti dopo <b>"SERIALI APPARATI:"</b> nel testo e quelli dopo <b>S/N</b> nelle foto.
            Oggetto con <b>"materiale ritirato"</b> → carico sul magazzino viaggiante del tecnico. Oggetto con <b>numero impianto o WR</b> → seriale installato con l'oggetto nelle note.
            Se non trova seriali compare un avviso in Dashboard.
          </p>
        </div>
        <div className="flex gap-2">
        <Button variant="outline" onClick={stopNow} disabled={!config?.running} className="rounded-sm border-red-300 text-red-600 hover:bg-red-50" data-testid="mail-stop-btn">
          <Square className="mr-2 h-4 w-4" /> Interrompi
        </Button>
        <Button onClick={runNow} disabled={!config?.has_password || config?.running} className="rounded-sm bg-[#0052FF] hover:bg-[#0043d1]" data-testid="mail-run-now-btn">
          {config?.running ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />}
          {config?.running ? (config.progress_total ? `Lettura in corso: ${config.progress_done} di ${config.progress_total}` : "Lettura in corso...") : "Leggi ora"}
        </Button>
        </div>
      </div>

      {config?.last_run_at && (
        <div className={`rounded-sm border p-3 text-sm ${config.last_error ? "border-red-200 bg-red-50 text-red-700" : "border-emerald-200 bg-emerald-50 text-emerald-800"}`} data-testid="mail-last-run">
          Ultima lettura: {new Date(config.last_run_at).toLocaleString("it-IT")} · {config.last_error || config.last_run_status}
        </div>
      )}

      <MailConfigCard config={config} onSaved={setConfig} />

      <DetailedCheckCard techs={techs} running={config?.running} onStarted={() => { setConfig((c) => ({ ...c, running: true })); setTimeout(() => load().catch(() => {}), 1500); }} />

      <div>
        <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
          <div className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500">Ultime mail elaborate</div>
          <Select value={techFilter} onValueChange={setTechFilter}>
            <SelectTrigger className="h-9 w-64 rounded-sm" data-testid="mail-log-tech-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutti i tecnici</SelectItem>
              {techs.filter((t) => (config?.technician_ids || []).includes(t.id)).map((t) => (
                <SelectItem key={t.id} value={t.id} data-testid={`mail-log-tech-option-${t.id}`}>{t.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <MailLogTable items={log} />
      </div>
    </div>
  );
}
